import React, { useState, useEffect, useMemo } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { verfuegbareJahre, monatsEinordnung } from '../lib/utils';
import { TILES, applyFilters } from '../lib/dashboardFilter';
import { ADMIN_MONAT_SCHLUESSEL, ADMIN_FILTER_SCHLUESSEL, gemerkterMonatAus, gemerkteFilterAus } from '../lib/dashboardMonat';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, timesheetsAPI, jugendaemterAPI, assignmentsAPI, usersAPI } from '../lib/api';
import { YearlyOverviewCards } from '../components/YearlyOverview';
import { VersionHistoryDialog } from '../components/VersionHistory';
import { useSortableData } from '../hooks/useSortableData';
import { SortableHeader } from '../components/SortableHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ListChecks,
  FileCheck,
  Ban,
  HelpCircle,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Download,
  RefreshCw,
  FileText,
  Loader2,
  Eye,
  RotateCcw,
  Euro,
  Plus,
  FileDown,
  Sheet,
  History,
  Search,
  Receipt,
  Archive,
  ArchiveRestore,
  CalendarClock,
  KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

// Gemerkter Monat und gemerkte Filter (Speicherzugriff in try/catch, privater Modus
// laedt einfach mit heutigem Monat und ohne Filter).
const gemerkterMonat = () => {
  try {
    return gemerkterMonatAus(localStorage.getItem(ADMIN_MONAT_SCHLUESSEL), verfuegbareJahre());
  } catch (_) {
    return null;
  }
};

const gemerkteFilter = () => {
  try {
    return gemerkteFilterAus(sessionStorage.getItem(ADMIN_FILTER_SCHLUESSEL), TILES.map(t => t.value));
  } catch (_) {
    return gemerkteFilterAus(null, TILES.map(t => t.value));
  }
};

const STATUS_RANK = { red: 0, yellow: 1, green: 2 };

const SORT_ACCESSORS = {
  status:      (it) => `${STATUS_RANK[it.ampel_status] ?? 0}_${(it.employee?.last_name || '').toLowerCase()}`,
  mitarbeiter: (it) => `${it.employee?.last_name || ''} ${it.employee?.first_name || ''}`.toLowerCase().trim(),
  stunden:     (it) => it.total_hours || 0,
};

const VOUCHER_ABGERECHNET = ['open', 'paid', 'paidoff', 'overdue'];
// Nur für die Anzeige — der Vergleich im Code läuft weiter über die englischen Werte.
const VOUCHER_LABEL = {
  draft: 'Entwurf', open: 'offen', paid: 'bezahlt',
  paidoff: 'ausgeglichen', overdue: 'überfällig', voided: 'storniert',
};
const voucherText = (s) => VOUCHER_LABEL[s] || s || '—';
// Text des Warnkastens im Abrechnungs-Dialog. Er erscheint immer, weil in JEDEM Fall
// eine E-Mail an die mitarbeitende Person ausgelöst wird.
const billingWarnText = (d) => {
  const nr = d.voucherNumber || '—';
  if (VOUCHER_ABGERECHNET.includes(d.voucherStatus)) {
    return `Laut Lexware existiert bereits eine Rechnung Nr. ${nr}, Status ${voucherText(d.voucherStatus)}. Das Häkchen ist eine zusätzliche Erklärung, keine Korrektur.`;
  }
  if (d.voucherStatus === 'voided') {
    return `Laut Lexware ist die Rechnung Nr. ${nr} storniert. Das Häkchen bestätigt trotzdem den Versand — es korrigiert nichts in Lexware.`;
  }
  if (d.voucherStatus === 'draft') {
    return `Laut Lexware liegt nur ein Entwurf Nr. ${nr} vor, also noch keine Rechnung. Das Häkchen ist Ihre eigene Erklärung.`;
  }
  return 'Zu diesem Stundenzettel ist kein Lexware-Belegstatus bekannt. Das Häkchen ist allein Ihre Erklärung — es prüft nichts in Lexware nach.';
};
const datumDe = (iso) => {
  const t = String(iso || '').slice(0, 10).split('-');
  return t.length === 3 ? `${t[2]}.${t[1]}.${t[0]}` : '—';
};

const DEFAULT_SORT = { key: 'status', dir: 'asc' };

// Farbe und Icon je Filterwert; Beschriftung/Zaehlung kommen aus lib/dashboardFilter.js
const KACHEL_STIL = {
  alle:          { icon: ListChecks,  card: 'border-slate-200',                 text: 'text-slate-500',   value: 'text-slate-900',   ring: 'ring-slate-400' },
  green:         { icon: CheckCircle2, card: 'border-green-200 bg-green-50/50',  text: 'text-green-600',   value: 'text-green-700',   ring: 'ring-green-500' },
  yellow:        { icon: AlertCircle,  card: 'border-amber-200 bg-amber-50/50',  text: 'text-amber-600',   value: 'text-amber-700',   ring: 'ring-amber-500' },
  red:           { icon: XCircle,      card: 'border-red-200 bg-red-50/50',      text: 'text-red-600',     value: 'text-red-700',     ring: 'ring-red-500' },
  overtime:      { icon: Clock,        card: 'border-blue-200 bg-blue-50/50',    text: 'text-blue-600',    value: 'text-blue-700',    ring: 'ring-blue-500' },
  billed:        { icon: Euro,         card: 'border-emerald-200 bg-emerald-50/50', text: 'text-emerald-600', value: 'text-emerald-700', ring: 'ring-emerald-500' },
  lexware_check: { icon: Receipt,      card: 'border-red-200 bg-red-50/50',      text: 'text-red-600',     value: 'text-red-700',     ring: 'ring-red-500' },
  kind_krank:    { icon: AlertTriangle, card: 'border-red-200 bg-red-50/50',     text: 'text-red-600',     value: 'text-red-700',     ring: 'ring-red-500' },
};
const KACHEL_STIL_STUMM = { card: 'border-slate-200 bg-slate-50/40', text: 'text-slate-400', value: 'text-slate-400', ring: 'ring-slate-400' };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  // Das Buero prueft beim Monatsabschluss Zettel und soll beim Zurueckgehen nicht im
  // aktuellen Monat landen (Christian, 10.09.2026). Eigener Schluessel je Ansicht.
  const [selectedMonth, setSelectedMonth] = useState(() => gemerkterMonat()?.monat ?? new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => gemerkterMonat()?.jahr ?? new Date().getFullYear());
  const [reopenDialog, setReopenDialog] = useState({ open: false, id: null, name: '', reason: '' });
  const [reopening, setReopening] = useState(false);
  const [billingDialog, setBillingDialog] = useState({ open: false, timesheetId: null, name: '', isSent: false });
  const [versionDialog, setVersionDialog] = useState({ open: false, timesheetId: null, name: '', isSubmitted: false });
  const [billingDate, setBillingDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingMailBewusst, setBillingMailBewusst] = useState(false);
  const [creatingTimesheet, setCreatingTimesheet] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [activeView, setActiveView] = useState('month');
  const [yearlyData, setYearlyData] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [yearlyYear, setYearlyYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState(() => gemerkteFilter().search);
  const [statusFilter, setStatusFilter] = useState(() => gemerkteFilter().status);
  const [jugendamtFilter, setJugendamtFilter] = useState(() => gemerkteFilter().jugendamt);
  const [jugendaemter, setJugendaemter] = useState([]);
  const [archiveTsDialog, setArchiveTsDialog] = useState({ open: false, id: null, name: '' });
  const [archivingTs, setArchivingTs] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedList, setArchivedList] = useState([]);
  const [expiringCount, setExpiringCount] = useState(0);
  const [onboardingCount, setOnboardingCount] = useState(0);

  const timesheetArchiveReason = (ts) => {
    if (!ts) return 'Kein Stundenzettel vorhanden';
    if (ts.status !== 'offen') return 'Zettel ist bereits eingereicht';
    if (ts.has_employee_signature || ts.has_teacher_signature || ts.teacher_confirmed) return 'Zettel enthält Unterschriften';
    if (ts.billing_sent) return 'Zettel ist mit der Abrechnung verknüpft';
    const hasContent = (ts.entries || []).some((e) => (e.hours || 0) > 0 || (e.comment || '').trim());
    if (hasContent) return 'Zettel enthält Stunden oder Kommentare';
    return null;
  };

  const loadArchived = async () => {
    try {
      const res = await timesheetsAPI.getArchived();
      setArchivedList(res.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden der weggeräumten Stundenzettel');
    }
  };

  const toggleShowArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next) loadArchived();
  };

  const confirmArchiveTimesheet = async () => {
    try {
      setArchivingTs(true);
      await timesheetsAPI.archive(archiveTsDialog.id);
      toast.success('Stundenzettel weggeräumt');
      setArchiveTsDialog({ open: false, id: null, name: '' });
      fetchDashboard();
      if (showArchived) loadArchived();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Wegräumen'));
    } finally {
      setArchivingTs(false);
    }
  };

  const handleRestoreTimesheet = async (id) => {
    try {
      await timesheetsAPI.restore(id);
      toast.success('Stundenzettel zurückgeholt');
      loadArchived();
      fetchDashboard();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Zurückholen'));
    }
  };

  // Monat in localStorage (ueberlebt den naechsten Tag), Filter nur fuer die Sitzung im Tab.
  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_MONAT_SCHLUESSEL,
        JSON.stringify({ monat: selectedMonth, jahr: selectedYear }));
    } catch { /* privater Modus: dann eben ohne Gedaechtnis */ }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    try {
      sessionStorage.setItem(ADMIN_FILTER_SCHLUESSEL,
        JSON.stringify({ status: statusFilter, search, jugendamt: jugendamtFilter }));
    } catch { /* privater Modus: dann eben ohne Gedaechtnis */ }
  }, [statusFilter, search, jugendamtFilter]);

  useEffect(() => {
    jugendaemterAPI.getAll().then(r => {
      const liste = r.data || [];
      setJugendaemter(liste);
      // Ein gemerktes Jugendamt, das es nicht mehr gibt, wuerde die Tabelle leer zeigen.
      setJugendamtFilter(aktuell => (aktuell !== 'alle' && !liste.some(j => j.id === aktuell)
        ? 'alle' : aktuell));
    }).catch(() => {});
    assignmentsAPI.getExpiring().then(r => setExpiringCount(r.data?.count || 0)).catch(() => {});
    usersAPI.onboardingStatus().then(r => setOnboardingCount(r.data?.count_dringend || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [selectedMonth, selectedYear]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getAdminDashboard(selectedMonth, selectedYear);
      setDashboard(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleReopen = async () => {
    try {
      setReopening(true);
      if (reopenDialog.billingSent) {
        await timesheetsAPI.unmarkBillingSent(reopenDialog.id);
      }
      const reopenRes = await timesheetsAPI.reopen(reopenDialog.id, reopenDialog.reason);
      toast.success('Stundenzettel wurde wieder geöffnet');
      if (reopenRes?.data?.lexware_draft_remaining) {
        // 2.4: Hinweis ohne Fachbegriffe – ein Rechnungs-Beleg bleibt in Lexware zurück
        toast.warning(
          'Hinweis: In Lexware liegt noch ein Rechnungs-Beleg zu diesem Zeitraum. Bitte diesen dort löschen, bevor der Stundenzettel erneut eingereicht wird – sonst entsteht keine neue Rechnung.',
          { duration: 12000 }
        );
      }
      setReopenDialog({ open: false, id: null, name: '', reason: '' });
      fetchDashboard();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Öffnen'));
    } finally {
      setReopening(false);
    }
  };

  const downloadPDF = async (timesheetId) => {
    try {
      const response = await timesheetsAPI.getPDF(timesheetId);
      const { pdf, filename } = response.data;
      
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf}`;
      link.download = filename;
      link.click();
      
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim PDF-Download');
    }
  };

  const downloadReportPDF = async () => {
    try {
      setDownloadingReport(true);
      const response = await dashboardAPI.getDashboardPDF(selectedMonth, selectedYear);
      const { pdf, filename } = response.data;
      
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf}`;
      link.download = filename;
      link.click();
      
      toast.success('Report heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Report-Download');
    } finally {
      setDownloadingReport(false);
    }
  };

  const downloadReportExcel = async () => {
    try {
      setDownloadingReport(true);
      const response = await dashboardAPI.getDashboardExcel(selectedMonth, selectedYear);
      const { excel, filename } = response.data;
      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${excel}`;
      link.download = filename;
      link.click();
      toast.success('Excel heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Excel-Download');
    } finally {
      setDownloadingReport(false);
    }
  };

  const loadYearly = async (yr) => {
    try {
      setYearlyLoading(true);
      const res = await dashboardAPI.getYearlyOverview(yr);
      setYearlyData(res.data);
    } catch (e) {
      toast.error('Fehler beim Laden der Jahresübersicht');
    } finally {
      setYearlyLoading(false);
    }
  };

  const handleBillingConfirm = async () => {
    try {
      setBillingLoading(true);
      if (billingDialog.isSent) {
        // Undo billing sent
        await timesheetsAPI.unmarkBillingSent(billingDialog.timesheetId);
        toast.success('Abrechnungsstatus zurückgesetzt');
      } else {
        // Mark as billing sent
        await timesheetsAPI.markBillingSent(billingDialog.timesheetId, billingDate);
        toast.success('Abrechnung als verschickt markiert');
      }
      setBillingDialog({ open: false, timesheetId: null, name: '', isSent: false });
      setBillingMailBewusst(false);
      fetchDashboard();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCreateTimesheet = async (assignmentId) => {
    try {
      setCreatingTimesheet(assignmentId);
      await timesheetsAPI.createForEmployee({
        assignment_id: assignmentId,
        month: selectedMonth,
        year: selectedYear
      });
      toast.success('Stundenzettel erstellt');
      fetchDashboard();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Erstellen'));
    } finally {
      setCreatingTimesheet(null);
    }
  };

  const getAmpelIcon = (status) => {
    switch (status) {
      case 'green':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'yellow':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case 'red':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-slate-400" />;
    }
  };

  const getAmpelBadge = (status) => {
    const config = {
      green: { label: 'Eingereicht', className: 'bg-green-100 text-green-700 border-green-200' },
      yellow: { label: 'In Bearbeitung', className: 'bg-amber-100 text-amber-700 border-amber-200' },
      red: { label: 'Nicht erstellt', className: 'bg-red-100 text-red-700 border-red-200' },
    };
    const c = config[status] || config.red;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  // Jugendamt + Suche angewandt, Statusfilter noch nicht: Grundlage aller Kachelzahlen.
  const basis = useMemo(
    () => applyFilters(dashboard?.data || [], { status: 'alle', jugendamt: jugendamtFilter, search }),
    [dashboard, jugendamtFilter, search]
  );

  const filtered = useMemo(
    () => applyFilters(basis, { status: statusFilter, jugendamt: 'alle', search: '' }),
    [basis, statusFilter]
  );

  const { sorted, sortConfig, requestSort } = useSortableData(filtered, SORT_ACCESSORS, DEFAULT_SORT);
  const filtersActive = search.trim() !== '' || statusFilter !== 'alle' || jugendamtFilter !== 'alle';
  // Die Kachelzahlen stammen aus `basis` - ein reiner Statusfilter aendert sie nicht.
  const kachelnGefiltert = search.trim() !== '' || jugendamtFilter !== 'alle';
  const resetFilters = () => { setSearch(''); setStatusFilter('alle'); setJugendamtFilter('alle'); };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
              Admin Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              Übersicht aller Stundenzettel
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
              <SelectTrigger className="w-36" data-testid="admin-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((month, idx) => (
                  <SelectItem key={idx} value={String(idx + 1)}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24" data-testid="admin-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {verfuegbareJahre().map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={fetchDashboard} data-testid="refresh-button">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Daten aktualisieren</p>
              </TooltipContent>
            </Tooltip>

            <Button 
              className="bg-blue-800 hover:bg-blue-900"
              onClick={downloadReportPDF}
              disabled={downloadingReport}
              data-testid="download-report-button"
            >
              {downloadingReport ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              PDF Report
            </Button>

            <Button 
              variant="outline"
              onClick={downloadReportExcel}
              disabled={downloadingReport}
              data-testid="admin-excel-export"
            >
              {downloadingReport ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sheet className="h-4 w-4 mr-2" />
              )}
              Excel-Export
            </Button>
          </div>
        </div>

        {monatsEinordnung(selectedMonth, selectedYear) === 'sonstiges' && (
          <div className="flex items-center gap-3 flex-wrap rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="admin-falscher-monat-band">
            <span>
              <strong>Sie sehen {monthNames[selectedMonth - 1]} {selectedYear}.</strong>{' '}
              Heute ist der {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}.
            </span>
            <Button variant="outline" size="sm" data-testid="admin-zum-aktuellen-monat"
              onClick={() => {
                const heute = new Date();
                setSelectedMonth(heute.getMonth() + 1);
                setSelectedYear(heute.getFullYear());
              }}>
              Zum aktuellen Monat
            </Button>
          </div>
        )}

        {expiringCount > 0 && (
          <button
            type="button"
            onClick={() => navigate('/admin/assignments?filter=expiring')}
            className="w-full flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left hover:bg-amber-100 transition-colors"
            data-testid="expiring-dashboard-hint"
          >
            <CalendarClock className="h-5 w-5 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-800">
              <span className="font-semibold">{expiringCount}</span> {expiringCount === 1 ? 'Bewilligung läuft' : 'Bewilligungen laufen'} in den nächsten 4 Wochen ab.
            </span>
            <span className="ml-auto text-xs text-amber-700 underline">Ansehen</span>
          </button>
        )}

        {onboardingCount > 0 && (
          <button
            type="button"
            onClick={() => navigate('/admin/users?filter=never-logged-in')}
            className="w-full flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left hover:bg-red-100 transition-colors"
            data-testid="onboarding-dashboard-hint"
          >
            <KeyRound className="h-5 w-5 text-red-600 shrink-0" />
            <span className="text-sm text-red-800">
              <span className="font-semibold">{onboardingCount}</span> {onboardingCount === 1 ? 'Person braucht' : 'Personen brauchen'} dringend einen Zugang.
            </span>
            <span className="ml-auto text-xs text-red-700 underline">Ansehen</span>
          </button>
        )}

        <Tabs value={activeView} onValueChange={(v) => { setActiveView(v); if (v === 'year' && !yearlyData) loadYearly(yearlyYear); }}>
          <TabsList className="grid w-full max-w-xs grid-cols-2">
            <TabsTrigger value="month">Monat</TabsTrigger>
            <TabsTrigger value="year" data-testid="yearly-overview-tab">Jahr</TabsTrigger>
          </TabsList>

          <TabsContent value="month" className="space-y-6 mt-4">

        {/* Summary Cards - Kacheln sind Filterknoepfe (Definition: lib/dashboardFilter.js) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {TILES.map((tile) => {
            const stil = KACHEL_STIL[tile.value];
            const Icon = stil.icon;
            const wert = tile.count(basis);
            const aktiv = statusFilter === tile.value;

            // Warnkachel: zaehlt zusaetzlich ungefiltert, bleibt rot solange irgendwo ein Fehler offen ist
            const istWarnung = tile.value === 'lexware_check' || tile.value === 'kind_krank';
            const gesamt = istWarnung ? tile.count(dashboard?.data || []) : null;
            const stumm = istWarnung && gesamt === 0;
            const s = stumm ? { ...stil, ...KACHEL_STIL_STUMM } : stil;

            let untertitel = tile.subtitle;
            if (istWarnung && gesamt !== wert) untertitel += ` (${gesamt} gesamt)`;

            return (
              <button
                key={tile.value}
                type="button"
                aria-pressed={aktiv}
                onClick={() => setStatusFilter(tile.value === 'alle' || !aktiv ? tile.value : 'alle')}
                className={`text-left rounded-xl transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-600 ${aktiv ? `ring-2 ring-offset-2 ${s.ring}` : ''}`}
                data-testid={`stat-${tile.value}`}
              >
                <Card
                  className={`stat-card h-full hover:shadow-md ${s.card}`}
                  {...(tile.value === 'lexware_check' ? { 'data-testid': 'lexware-error-summary-card' } : {})}
                >
                  <CardContent className="p-3">
                    <div className={`flex items-center gap-1.5 text-xs mb-1 ${s.text}`}>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      {tile.label}
                    </div>
                    <div className={`text-2xl font-bold ${s.value}`}>{wert}</div>
                    <p className={`text-[11px] mt-0.5 ${s.text}`}>{untertitel}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        {/* Warnings */}
        {summary.warnings > 0 && (
          <div className="warning-banner p-4 rounded-lg flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">
                Achtung: {summary.warnings} Krankheitswarnung(en)!
              </p>
              <p className="text-sm text-amber-700">
                Mitarbeiter mit mehr als 15 Krankheitstagen des betreuten Kindes.
              </p>
            </div>
          </div>
        )}

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Mitarbeiter Übersicht - {monthNames[selectedMonth - 1]} {selectedYear}</CardTitle>
            <CardDescription>Ampelsystem: Grün = Eingereicht, Gelb = In Bearbeitung, Rot = Nicht erstellt</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filter & Search Toolbar */}
            <div className="flex flex-col gap-3 mb-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
                {TILES.map(opt => (
                  <button key={opt.value} type="button" onClick={() => setStatusFilter(opt.value)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      statusFilter === opt.value ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-600 hover:text-slate-900'
                    }`} data-testid={`filter-status-${opt.value}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input placeholder="Mitarbeiter, Kind oder Schule…" value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 w-full sm:w-64" data-testid="dashboard-search" />
                </div>
                <Select value={jugendamtFilter} onValueChange={setJugendamtFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="filter-jugendamt"><SelectValue placeholder="Jugendamt" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle Jugendämter</SelectItem>
                    {jugendaemter.map(j => (<SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Button
                  variant={showArchived ? 'default' : 'outline'}
                  size="sm"
                  className={showArchived ? 'bg-slate-700 hover:bg-slate-800' : ''}
                  onClick={toggleShowArchived}
                  data-testid="timesheet-archived-filter"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archivierte {showArchived ? 'ausblenden' : 'anzeigen'}
                </Button>
              </div>
            </div>
            {showArchived && (
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="archived-timesheets-section">
                <p className="text-sm font-medium text-slate-700 mb-2">Weggeräumte Stundenzettel ({archivedList.length})</p>
                {archivedList.length === 0 ? (
                  <p className="text-sm text-slate-500">Aktuell sind keine Stundenzettel weggeräumt.</p>
                ) : (
                  <div className="space-y-1">
                    {archivedList.map((ts) => (
                      <div key={ts.id} className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-sm border border-slate-100">
                        <div>
                          <span className="font-medium text-slate-800">{ts.child_name || 'Unbekannt'}</span>
                          <span className="text-slate-500"> · {monthNames[(ts.month || 1) - 1]} {ts.year}</span>
                          {ts.archived_by_name && (
                            <span className="text-xs text-slate-400"> · weggeräumt von {ts.archived_by_name}</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-emerald-600 hover:text-emerald-700"
                          onClick={() => handleRestoreTimesheet(ts.id)}
                          data-testid={`restore-timesheet-${ts.id}`}
                        >
                          <ArchiveRestore className="h-4 w-4 mr-1" />
                          Zurückholen
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
              {kachelnGefiltert && (
                <span className="text-amber-800 bg-amber-100 border border-amber-300 rounded px-2 py-0.5"
                  data-testid="admin-filter-aktiv">
                  Filter aktiv - die Kachelzahlen zeigen nur die gefilterten Einträge.
                </span>
              )}
              <span>{sorted.length} von {dashboard?.data?.length || 0} Einträgen</span>
              {filtersActive && (
                <button type="button" onClick={resetFilters} className="text-blue-700 hover:underline" data-testid="filter-reset">
                  Filter zurücksetzen
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader label="Mitarbeiter" sortKey="mitarbeiter" sortConfig={sortConfig} onSort={requestSort} />
                    <TableHead>Kind / Schule</TableHead>
                    <SortableHeader label="Stunden" sortKey="stunden" sortConfig={sortConfig} onSort={requestSort} align="right" />
                    <TableHead className="text-center">Abrechnung</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        {(dashboard?.data?.length || 0) === 0 ? 'Keine Daten für diesen Monat' : 'Keine Treffer für die aktuellen Filter'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sorted.map((item, index) => (
                      <TableRow 
                        key={item.assignment?.id || index}
                        className={item.warning ? 'bg-red-50/50' : ''}
                        data-testid={`dashboard-row-${index}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getAmpelIcon(item.ampel_status)}
                            {getAmpelBadge(item.ampel_status)}
                            {item.has_overtime && (
                              <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid={'overtime-badge-' + (item.timesheet ? item.timesheet.id : '')}>
                                Mehrstunden
                              </Badge>
                            )}
                            {item.stunden_hinweise?.length > 0 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs"
                                    data-testid={`stunden-hinweis-badge-${item.timesheet.id}`}>
                                    Stunden prüfen
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {item.stunden_hinweise.map(h => h.date.split('-').reverse().join('.')).join(', ')}
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {item.kind_krank_warnung && item.timesheet && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] cursor-help" data-testid={'kind-krank-badge-' + item.timesheet.id}>
                                    Kind krank: {item.kind_krank_warnung.anzahl} von {item.kind_krank_warnung.limit}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs">{item.kind_krank_warnung.anzahl} Tage „Betr. Kind krank“ in eingereichten Stundenzetteln dieses Schuljahres. Tage über dem Limit werden nicht abgerechnet. Bitte prüfen.</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {item.timesheet?.lexware_status === 'error' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] cursor-help" data-testid={'lexware-error-badge-' + item.timesheet.id}>
                                    Rechnung: Fehler
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs">{item.timesheet.lexware_error || 'Unbekannter Fehler'}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {item.timesheet?.lexware_status === 'conflict' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] cursor-help" data-testid={'lexware-conflict-badge-' + item.timesheet.id}>
                                    Rechnung: Konflikt
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs">{item.timesheet.lexware_error || 'In Lexware liegt noch ein Beleg zu diesem Zeitraum.'}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {item.timesheet?.lexware_status === 'unchecked' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] cursor-help" data-testid={'lexware-unchecked-badge-' + item.timesheet.id}>
                                    Rechnung: ungeprüft
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs">{item.timesheet.lexware_error || 'Rechnungsstatus in Lexware nicht erreichbar.'}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {item.timesheet?.status === 'eingereicht' && (item.timesheet?.lexware_status === 'ok' || (!item.timesheet?.lexware_status && item.timesheet?.lexware_invoice_id)) && (
                              <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]" data-testid={'lexware-ok-badge-' + item.timesheet.id}>
                                Rechnung erstellt
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">
                              {item.employee?.first_name} {item.employee?.last_name}
                            </p>
                            <p className="text-sm text-slate-500">{item.employee?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{item.assignment?.child_name}</p>
                            <p className="text-sm text-slate-500">{item.assignment?.school_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{item.total_hours?.toFixed(1) || '0.0'}</span>
                          <span className="text-slate-500 text-sm"> / {item.monthly_target?.toFixed(1) || '0'}</span>
                          {(() => {
                            const ist = item.total_hours || 0;
                            const soll = item.monthly_target || 0;
                            if (!soll || soll <= 0) return null;
                            const pctRaw = (ist / soll) * 100;
                            const dev = Math.abs(ist - soll) / soll * 100;
                            const color = dev <= 10 ? 'bg-green-500' : dev <= 20 ? 'bg-yellow-500' : 'bg-red-500';
                            const pct = Math.max(0, Math.min(100, pctRaw));
                            return (
                              <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1.5 overflow-hidden" data-testid="dashboard-progress-bar">
                                <div className={`h-1.5 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center">
                          {item.timesheet ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => setBillingDialog({
                                    open: true,
                                    timesheetId: item.timesheet.id,
                                    name: `${item.employee?.first_name} ${item.employee?.last_name}`,
                                    isSent: item.billing_sent,
                                    voucherStatus: item.lexware_voucher_status || '',
                                    voucherNumber: item.lexware_voucher_number || '',
                                    voucherCheckedAt: item.lexware_voucher_checked_at || ''
                                  })}
                                  className="inline-flex items-center gap-1 p-2 rounded-full transition-colors hover:bg-slate-100"
                                  data-testid={`billing-button-${index}`}
                                >
                                  <Euro className="h-4 w-4 text-slate-600" />
                                  {item.billing_sent ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" data-testid={`billing-sent-icon-${index}`} />
                                  ) : VOUCHER_ABGERECHNET.includes(item.lexware_voucher_status) ? (
                                    <FileCheck className="h-4 w-4 text-blue-600" data-testid={`billing-voucher-icon-${index}`} />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" data-testid={`billing-unsent-icon-${index}`} />
                                  )}
                                  {item.lexware_voucher_status === 'voided' && (
                                    <Ban className="h-4 w-4 text-red-600" data-testid={`billing-voided-icon-${index}`} />
                                  )}
                                  {item.lexware_voucher_vermisst_seit && (
                                    <HelpCircle className="h-4 w-4 text-amber-500" data-testid={`billing-missing-icon-${index}`} />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div>
                                  {item.billing_sent ? (
                                    <>
                                      <p className="font-medium">Abrechnung verschickt</p>
                                      <p className="text-xs">am {item.billing_sent_date}</p>
                                      <p className="text-xs">von {item.billing_sent_by}</p>
                                    </>
                                  ) : (
                                    <p>Abrechnung als verschickt markieren</p>
                                  )}
                                  {item.lexware_voucher_status === 'voided' && (
                                    <p className="text-xs mt-1">laut Lexware storniert</p>
                                  )}
                                  {VOUCHER_ABGERECHNET.includes(item.lexware_voucher_status) && (
                                    <p className="text-xs mt-1">
                                      {`laut Lexware: ${voucherText(item.lexware_voucher_status)}, Nr. ${item.lexware_voucher_number || '—'}, geprüft am ${datumDe(item.lexware_voucher_checked_at)}`}
                                    </p>
                                  )}
                                  {item.lexware_voucher_vermisst_seit && (
                                    <p className="text-xs mt-1">
                                      {`Beleg in Lexware nicht mehr auffindbar (seit ${datumDe(item.lexware_voucher_vermisst_seit)})`}
                                    </p>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {item.timesheet ? (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => navigate(`/timesheet/${item.timesheet.id}`)}
                                      data-testid={`view-timesheet-${index}`}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Stundenzettel ansehen</p>
                                  </TooltipContent>
                                </Tooltip>

                                {(() => {
                                  const reason = timesheetArchiveReason(item.timesheet);
                                  const canArchive = reason === null;
                                  return (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={!canArchive}
                                            onClick={() => canArchive && setArchiveTsDialog({
                                              open: true,
                                              id: item.timesheet.id,
                                              name: `${item.employee?.first_name} ${item.employee?.last_name}`,
                                            })}
                                            data-testid={`archive-timesheet-${item.timesheet.id}`}
                                          >
                                            <Archive className="h-4 w-4" />
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{canArchive ? 'Stundenzettel wegräumen' : reason}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                })()}
                                
                                {item.timesheet.status === 'eingereicht' && (
                                  <>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => downloadPDF(item.timesheet.id)}
                                          data-testid={`download-timesheet-${index}`}
                                        >
                                          <Download className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Stundenzettel herunterladen</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setReopenDialog({
                                            open: true,
                                            id: item.timesheet.id,
                                            name: `${item.employee?.first_name} ${item.employee?.last_name}`,
                                            billingSent: item.billing_sent,
                                            billingDate: item.billing_sent_date,
                                          })}
                                          data-testid={`reopen-timesheet-${index}`}
                                        >
                                          <RotateCcw className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Stundenzettel wieder öffnen</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setVersionDialog({
                                            open: true,
                                            timesheetId: item.timesheet.id,
                                            name: `${item.employee?.first_name} ${item.employee?.last_name}`,
                                            isSubmitted: item.timesheet.status === 'eingereicht'
                                          })}
                                          data-testid={`version-history-${index}`}
                                        >
                                          <History className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Versionen</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </>
                                )}
                              </>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCreateTimesheet(item.assignment?.id)}
                                    disabled={creatingTimesheet === item.assignment?.id}
                                    data-testid={`create-timesheet-${index}`}
                                  >
                                    {creatingTimesheet === item.assignment?.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Plus className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Stundenzettel erstellen</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

          </TabsContent>

          <TabsContent value="year" className="space-y-6 mt-4">
            <div className="flex items-center gap-4">
              <Select value={String(yearlyYear)} onValueChange={(v) => { setYearlyYear(Number(v)); loadYearly(Number(v)); }}>
                <SelectTrigger className="w-24" data-testid="yearly-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {verfuegbareJahre().map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 italic">Nur für den internen Gebrauch (Backoffice).</p>
            </div>

            {yearlyLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-48" />
                <Skeleton className="h-48" />
              </div>
            ) : !yearlyData || yearlyData.rows.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  Keine aktiven Einsätze für dieses Jahr.
                </CardContent>
              </Card>
            ) : (
              <YearlyOverviewCards rows={yearlyData.rows} year={yearlyYear} />
            )}
          </TabsContent>
        </Tabs>

        {/* Reopen Dialog */}
        <AlertDialog open={reopenDialog.open} onOpenChange={(open) => setReopenDialog(prev => ({ ...prev, open }))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Stundenzettel wieder öffnen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie den Stundenzettel von {reopenDialog.name} wieder zur Bearbeitung freigeben?
                Der Mitarbeiter kann dann erneut Änderungen vornehmen und wird per E-Mail benachrichtigt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {reopenDialog.billingSent && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="reopen-billing-warning">
                <strong>Achtung:</strong> Dieser Stundenzettel wurde bereits abgerechnet
                {reopenDialog.billingDate ? ` (am ${reopenDialog.billingDate})` : ''}.
                Wenn Sie ihn jetzt ändern, weichen die internen Daten von der bereits versendeten Abrechnung ab.
                Der Abrechnungsstatus wird automatisch zurückgesetzt.
              </div>
            )}
            <div className="py-2">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Grund der Rückgabe (wird dem Mitarbeiter per E-Mail mitgeteilt)</label>
              <Textarea
                placeholder="Optional: Grund angeben..."
                value={reopenDialog.reason}
                onChange={(e) => setReopenDialog(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
                data-testid="reopen-reason-input"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReopen}
                disabled={reopening}
                className="bg-blue-800 hover:bg-blue-900"
              >
                {reopening ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Ja, wieder öffnen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Archive Timesheet Confirmation */}
        <AlertDialog open={archiveTsDialog.open} onOpenChange={(open) => setArchiveTsDialog(prev => ({ ...prev, open }))}>
          <AlertDialogContent data-testid="archive-timesheet-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Stundenzettel wegräumen?</AlertDialogTitle>
              <AlertDialogDescription>
                Der Stundenzettel von {archiveTsDialog.name} wird weggeräumt, aber nicht gelöscht.
                Du kannst ihn jederzeit zurückholen.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmArchiveTimesheet}
                disabled={archivingTs}
                className="bg-slate-700 hover:bg-slate-800"
                data-testid="confirm-archive-timesheet-button"
              >
                {archivingTs ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Wegräumen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Billing Confirmation Dialog */}
        <Dialog open={billingDialog.open} onOpenChange={(open) => { setBillingDialog(prev => ({ ...prev, open })); if (!open) setBillingMailBewusst(false); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Euro className="h-5 w-5 text-emerald-600" />
                {billingDialog.isSent ? 'Abrechnungsstatus zurücksetzen' : 'Abrechnung verschickt'}
              </DialogTitle>
              <DialogDescription>
                Stundenzettel von: <span className="font-medium">{billingDialog.name}</span>
              </DialogDescription>
            </DialogHeader>

            {billingDialog.isSent ? (
              <div className="py-4">
                <p className="text-slate-700">
                  Möchten Sie den Abrechnungsstatus für diesen Stundenzettel zurücksetzen?
                </p>
                {VOUCHER_ABGERECHNET.includes(billingDialog.voucherStatus) && (
                  <p className="mt-2 text-sm text-amber-800" data-testid="billing-reset-voucher-hint">
                    {`Hinweis: Laut Lexware existiert eine Rechnung (Status ${voucherText(billingDialog.voucherStatus)}). Der Zettel zählt deshalb weiterhin als abgerechnet.`}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg text-sm text-amber-900"
                  data-testid="billing-voucher-warning">
                  {billingWarnText(billingDialog)}
                </div>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-emerald-800 font-medium">
                    Ich versichere, dass die Abrechnung verschickt wurde.
                  </p>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="mt-1" checked={billingMailBewusst}
                    onChange={(e) => setBillingMailBewusst(e.target.checked)}
                    data-testid="billing-mail-ack" />
                  Mir ist bewusst, dass die mitarbeitende Person eine E-Mail erhält.
                </label>

                <div>
                  <Label htmlFor="billing-date">Datum der Abrechnung</Label>
                  <Input
                    id="billing-date"
                    type="date"
                    value={billingDate}
                    onChange={(e) => setBillingDate(e.target.value)}
                    data-testid="billing-date-input"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => { setBillingDialog({ open: false, timesheetId: null, name: '', isSent: false }); setBillingMailBewusst(false); }}>
                Abbrechen
              </Button>
              <Button
                className={billingDialog.isSent
                  ? 'bg-red-600 hover:bg-red-700'
                  : (VOUCHER_ABGERECHNET.includes(billingDialog.voucherStatus)
                      ? 'bg-slate-700 hover:bg-slate-800'
                      : 'bg-emerald-600 hover:bg-emerald-700')}
                onClick={handleBillingConfirm}
                disabled={billingLoading || (!billingDialog.isSent && !billingMailBewusst)}
                data-testid="billing-confirm-button"
              >
                {billingLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {billingDialog.isSent ? 'Status zurücksetzen' : 'Bestätigen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <VersionHistoryDialog
          open={versionDialog.open}
          onClose={() => setVersionDialog({ open: false, timesheetId: null, name: '', isSubmitted: false })}
          timesheetId={versionDialog.timesheetId}
          timesheetName={versionDialog.name}
          isSubmitted={versionDialog.isSubmitted}
          onRestored={fetchDashboard}
        />
      </div>
    </TooltipProvider>
  );
};

export default AdminDashboard;
